import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Heart,
    Baby,
    Sparkles,
    Sun,
    Smile,
    Layers,
    Home,
    Calendar,
    Users,
    Image as ImageIcon,
    Gift
} from 'lucide-react';

const maternityServices = [
    { id: '01', icon: Heart, title: 'Golden Hour Maternity', desc: 'Celebrating the raw beauty of motherhood against breathtaking outdoor backdrops and ethereal sun-drenched lighting.' },
    { id: '02', icon: Sparkles, title: 'Fine Art Studio Maternity', desc: 'Editorial-style, elegantly draped portraits utilizing soft shadow-sculpting to highlight your incredible silhouette.' },
    { id: '03', icon: Baby, title: 'In-Studio Posed Newborns', desc: 'Safe, deeply patient, and delicately styled sessions capturing those fleeting, sleepy early days within a heated studio.' },
    { id: '04', icon: Home, title: 'Lifestyle Home New-Family', desc: 'Unscripted, organic storytelling capturing your family bonding, feeding, and cuddling right inside your natural nursery ecosystem.' },
    { id: '05', icon: Smile, title: 'Baby Milestone & Sitter Sessions', desc: 'Commemorating the milestone markers—from first pushing up and tummy time smiles to joyful 6-to-9 month sitter phases.' },
    { id: '06', icon: Gift, title: 'The First Birthday Cake Smash', desc: 'A delightfully messy, high-energy celebratory session filled with gorgeous custom setups, tiny cakes, and pure baby chaos.' },
];

const familyMilestoneServices = [
    { id: '01', icon: Users, title: 'Generational Family Portfolios', desc: 'Honoring your roots by weaving grandparents, parents, and newborns together in cohesive, heartwarming legacy portraits.' },
    { id: '02', icon: Calendar, title: 'First-Year Baby Registries', desc: 'A curated multi-session structural package mapping growth curves across maternity, newborn, 6-month, and 1-year phases.' },
    { id: '03', icon: Sun, title: 'Seasonal Outdoor Family Fields', desc: 'Lively, playful, and beautifully candid evening run-arounds in natural meadows designed to capture real childhood magic.' },
    { id: '04', icon: Layers, title: 'Luxury Heirloom Folios & Albums', desc: 'Translating digital frames into tactile, flush-mount luxury albums wrapped in organic linen or soft genuine leathers.' },
    { id: '05', icon: Camera, title: 'Fresh 48 Hospital Documentaries', desc: 'Quiet, photojournalistic coverage inside the first 48 hours of birth right at the hospital room or birthing center.' },
];

const maternityDeliverables = [
    { id: '01', icon: Baby, title: 'Certified Safe Infant Handling', desc: 'Strict adherence to physiological baby-safety practices, secure structural spotting, and hybrid compound compositing.' },
    { id: '02', icon: Home, title: 'Thermal-Regulated Studio Comfort', desc: 'Maintaining a constant, closely monitored 78°F / 25°C studio ecosystem optimized to keep naked infants calm and comfortable.' },
    { id: '03', icon: Sparkles, title: 'High-Fidelity Organic Retouching', desc: 'Flawless skin evening and micro-toning that smooths imperfections while keeping natural skin texture, stretch marks, and tones authentic.' },
    { id: '04', icon: Layers, title: 'Curated Studio Wardrobe Access', desc: 'Complimentary access to a high-end curation of luxury lace gowns, silk wraps, and organic cotton newborn swaddles.' },
    { id: '05', icon: Smile, title: 'Flexible Multi-Paced Scheduling', desc: 'Sessions explicitly built with extended buffers to comfortably allow for unhurried feeding, soothing, and diaper adjustments.' },
    { id: '06', icon: Users, title: 'Guided Partner & Sibling Integration', desc: 'Easy-to-follow, stress-free prompts designed to help nervous dads and toddlers interact genuinely on camera.' },
    { id: '07', icon: Sun, title: 'Advanced Soft-Ambiance Diffusion', desc: 'Utilizing massive, ultra-feathered light modifiers to mimic gentle window light that never strains or startles sensitive newborn eyes.' },
    { id: '08', icon: Gift, title: 'Digital Announcement Fine-Tuning', desc: 'Rapid-turnaround preview galleries delivered quickly for seamless inclusion on birth announcement prints or digital rollouts.' },
    { id: '09', icon: ImageIcon, title: 'Permanent Digital Vault Preservation', desc: 'Archiving your family assets across redundant cloud systems to ensure your precious milestones remain secure for decades.' },
];

const MaternityAndBaby = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.5;
        const speedRight = 0.3;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Maternity & Baby Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Capture life's most precious moments with KhultStudios. We offer professional maternity, newborn, baby, toddler, kids, family, and milestone photography in Hyderabad with creative indoor and outdoor photoshoots."
                />

                <meta
                    name="keywords"
                    content="Maternity Photography Hyderabad, Baby Photography Hyderabad, Newborn Photography Hyderabad, Pregnancy Photoshoot Hyderabad, Baby Photoshoot Hyderabad, Kids Photography Hyderabad, Toddler Photography, Family Photography Hyderabad, Baby Milestone Photography, Cake Smash Photography, Infant Photography, Motherhood Photography, Child Photography, Family Portrait Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/maternity-and-baby-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/maternity-and-baby-photography"
                />
                <meta
                    property="og:title"
                    content="Maternity & Baby Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional maternity, newborn, baby, and family photography in Hyderabad. Preserve every milestone with timeless portraits by KhultStudios."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/maternity-baby-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Maternity & Baby Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Beautiful maternity, newborn, baby, and family photography sessions in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/maternity-baby-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/maternity_teaser.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Maternity, Newborn & Family Storytellers
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Preserving the Sacred Wonders of New Life
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        The quiet anticipation of a heartbeat, the soft fuzz on a newborn's shoulder, the eruptive joy of a first-year smash. These aren't just moments—they are the founding chapters of your family's grand legacy.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we blend fine-art safety architecture with warm, patient, and deeply comforting session design. We walk hand-in-hand with expecting mothers and new parents to preserve these incredibly fleeting milestones with timeless clarity.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Milestones
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519689680058-324335c77eba?q=80&w=500" alt="Beautiful Glowing Maternity Session at Sunset" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1544126592-807d8139d7ff?q=80&w=500" alt="Sleeping Newborn Wrapped Silky Cloth" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1531068691243-d78ea245c90b?q=80&w=500" alt="Pregnant Mother Elegantly Draped Portrait" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1555252333-9f8e92e65df9?q=80&w=500" alt="Baby Tummy Time 6 Month Milestone Smile" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519689680058-324335c77eba?q=80&w=500" alt="Beautiful Glowing Maternity Session at Sunset Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1544126592-807d8139d7ff?q=80&w=500" alt="Sleeping Newborn Wrapped Silky Cloth Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1531068691243-d78ea245c90b?q=80&w=500" alt="Pregnant Mother Elegantly Draped Portrait Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1555252333-9f8e92e65df9?q=80&w=500" alt="Baby Tummy Time 6 Month Milestone Smile Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography4.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography5.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography6.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/maternity-and-baby/maternity-and-baby-photography7.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* EXPANDED MATERNITY & BABY SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">✨ Specialized Maternity & Milestone Portfolios</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Children grow in the blink of an eye. Our thoughtfully structured milestone modules are engineered to handle the delicate intricacies of little ones, ensuring every chuckle and sleepy yawns are frozen perfectly in time.
                    </p>
                    <div className="row g-4 mt-2">
                        {maternityServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 2 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INSTITUTIONAL FAMILY CONNECTIONS */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">💝 Generational Preservation & Heirloom Systems</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Beyond single snapshots lies your unfolding story. From multi-session developmental registry tracks to high-end leather collections, we handcraft assets meant to be passed down through hands for generations.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {familyMilestoneServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MATERNITY OPERATIONAL COMPREHENSIVE MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">🍼 Complete Studio Comforts & Experience Deliverables</h2>
                    <div className="row g-4">
                        {maternityDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MaternityAndBaby;